import {
  BotMessage,
  Chat,
  CollaboratorChat,
  CustomerMessage,
  PersistentMessage,
  ThinChatSummary,
} from "@/types/chat"
import type { SampleQuestion } from "@/types/place"
import { TripRef } from "@/types/trip"
import { UserRef } from "@/types/user"
import { createSelector } from "@reduxjs/toolkit"

import { PagingMeta } from "@/lib/paging"
import type { IdeasTarget } from "@/lib/storage/detail-sheet-slice"
import { RootState } from "@/lib/storage/redux-store"
import { baseApi } from "./base-api"

export interface GetChatsResponse {
  chats: ThinChatSummary[]
  meta: PagingMeta
}

export interface IdeasQuestions {
  questions: SampleQuestion[]
  hasMemories: boolean
}

// trip_ref is present only for a trip the user sees.
export type ActionPillAnswerResponse =
  | {
      status: "streaming"
      chat_id: number
      stream_id: string
      label: string
      trip_ref?: TripRef
    }
  | {
      status: "ready"
      chat_id: number
      message: BotMessage
      label: string
      trip_ref?: TripRef
    }

export interface GetChatsArgs {
  trip?: TripRef | "individual"
  search?: string
  perPage?: number
}

export interface CollaboratorChatArgs {
  tripRef: TripRef
  chatId: number
}

interface RefreshFlightSearchArgs {
  chatId: number
  messageId: number
}

interface RefreshFlightSearchResponse {
  stream_id?: string
  customer_message: CustomerMessage
}

export const chatsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getActionPillAnswer: builder.query<ActionPillAnswerResponse, string>({
      query: (pillRef) => ({
        url: "/api/chats/create_ephemeral",
        method: "POST",
        data: { action_pill_ref: pillRef },
      }),
      keepUnusedDataFor: 300,
    }),
    getEphemeralLocationAnswer: builder.query<
      ActionPillAnswerResponse,
      { locationRef: string; ribbonRef?: string }
    >({
      query: ({ locationRef, ribbonRef }) => ({
        url: "/api/chats/create_ephemeral_for_location",
        method: "POST",
        data: { location_ref: locationRef, ribbon_ref: ribbonRef },
      }),
      keepUnusedDataFor: 300,
    }),
    getEphemeralPlaceQuestionAnswer: builder.query<
      ActionPillAnswerResponse,
      { entityRef: string; question: string; label?: string }
    >({
      query: ({ entityRef, question, label }) => ({
        url: "/api/chats/create_ephemeral_for_place_question",
        method: "POST",
        data: { entity_ref: entityRef, question, label },
      }),
      keepUnusedDataFor: 300,
    }),
    // The caller's ideas chat for a guide, created empty on first use. Not cached between
    // sheet opens so turns finished while the sheet was closed come back with it.
    getIdeasChat: builder.query<Chat, IdeasTarget>({
      query: (target) =>
        target.kind === "guide"
          ? {
              url: "/api/chats/create_ephemeral_for_guide",
              method: "POST",
              data: { inspiration_ref: target.inspirationRef },
            }
          : {
              url: "/api/chats/create_ephemeral_for_collection",
              method: "POST",
              data: { collection_ref: target.collectionRef },
            },
      transformResponse: (response: { chat: Chat }) => response.chat,
      keepUnusedDataFor: 0,
    }),
    getIdeasQuestions: builder.query<IdeasQuestions, IdeasTarget>({
      query: (target) =>
        target.kind === "guide"
          ? {
              url: "/api/chats/sample_questions_for_guide",
              params: { inspiration_ref: target.inspirationRef },
            }
          : {
              url: "/api/chats/sample_questions_for_collection",
              params: { collection_ref: target.collectionRef },
            },
      transformResponse: (response: {
        questions_v2?: SampleQuestion[]
        has_memories?: boolean
      }) => ({
        questions: response.questions_v2 ?? [],
        hasMemories: !!response.has_memories,
      }),
      keepUnusedDataFor: 0,
    }),
    getChatCount: builder.query<{ count: number }, void>({
      query: () => ({ url: "/api/chats/count" }),
      providesTags: ["Chats"],
      keepUnusedDataFor: 300,
    }),
    getChats: builder.infiniteQuery<GetChatsResponse, GetChatsArgs, number>({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage) => lastPage.meta.next_page ?? undefined,
        // Chat mutations affect the first page. Refetching only that page also
        // clears previously loaded pages so the list restarts from the top.
        refetchCachedPages: false,
      },
      query: ({ queryArg: args, pageParam }) => {
        const params = new URLSearchParams()
        if (args.trip) params.append("trip", args.trip)
        if (args.search) params.append("search", args.search)
        if (pageParam > 1) params.append("page", pageParam.toString())
        if (args.perPage) params.append("per_page", args.perPage.toString())
        const queryString = params.toString()
        const url = queryString ? `/api/chats?${queryString}` : "/api/chats"
        return { url }
      },
      providesTags: ["Chats"],
      serializeQueryArgs: ({ queryArgs }) => {
        const searchKey = queryArgs.search || "default"
        const tripKey = queryArgs.trip || "all"
        const perPageKey = queryArgs.perPage || "default"
        if (tripKey === "all" || tripKey === "individual") {
          return `${tripKey}:${searchKey}:${perPageKey}`
        }
        return `trip:${tripKey}:${searchKey}:${perPageKey}`
      },
      keepUnusedDataFor: 300,
    }),
    // Promotes an ephemeral answer chat into normal chat surfaces
    // (idempotent). Invalidates chat lists so the chat appears in them.
    promoteChat: builder.mutation<void, number>({
      query: (chatId) => ({
        url: `/api/chats/${chatId}/promote`,
        method: "PUT",
      }),
      invalidatesTags: ["Chats"],
    }),
    // One trip member's chats, a page at a time
    getCollaboratorChats: builder.infiniteQuery<
      { chats: CollaboratorChat[]; meta: PagingMeta },
      { tripRef: TripRef; userRef: UserRef; perPage: number },
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage) => lastPage.meta.next_page ?? undefined,
        refetchCachedPages: false,
      },
      query: ({ queryArg: { tripRef, userRef, perPage }, pageParam }) => {
        const params = new URLSearchParams({
          user_ref: userRef,
          per_page: perPage.toString(),
        })
        if (pageParam > 1) params.append("page", pageParam.toString())
        return { url: `/api/trips/${tripRef}/collaborator-chats?${params}` }
      },
      providesTags: ["Chats"],
    }),
    // Pages run backward in time: each next page holds earlier messages.
    getCollaboratorChatMessages: builder.infiniteQuery<
      {
        messages: PersistentMessage[]
        more?: boolean
        // the group chat continuing this chat, if there is one
        group_chat_id?: number
      },
      CollaboratorChatArgs,
      number | undefined
    >({
      infiniteQueryOptions: {
        initialPageParam: undefined,
        getNextPageParam: (lastPage) =>
          lastPage.more ? lastPage.messages[0]?.id : undefined,
      },
      query: ({ queryArg: { tripRef, chatId }, pageParam }) => ({
        url: `/api/trips/${tripRef}/collaborator-chats/${chatId}/messages${
          pageParam ? `?before_id=${pageParam}` : ""
        }`,
      }),
      providesTags: ["Chats"],
    }),
    // Opens the group chat continuing a collaborator's chat, creating it the
    // first time.
    openCollaboratorChatInGroup: builder.mutation<
      { chat_id: number },
      CollaboratorChatArgs
    >({
      query: ({ tripRef, chatId }) => ({
        url: `/api/trips/${tripRef}/collaborator-chats/${chatId}/group-chat`,
        method: "POST",
      }),
      invalidatesTags: ["Chats"],
    }),
    refreshFlightSearch: builder.mutation<
      RefreshFlightSearchResponse,
      RefreshFlightSearchArgs
    >({
      query: ({ chatId, messageId }) => ({
        url: `/api/chats/${chatId}/refresh-flight-search`,
        method: "POST",
        data: { message_id: messageId },
      }),
    }),
  }),
})

const selectChatCountResult = chatsApi.endpoints.getChatCount.select()

export const selectChatCount = createSelector(
  selectChatCountResult,
  (queryResult) => queryResult.data?.count ?? 0
)

/**
 * The api's `queries` cache holds results from every endpoint (e.g. getChatCount),
 * typed as `unknown`. Runtime-validate the shape before reading `chats`.
 */
function isGetChatsResponse(data: unknown): data is GetChatsResponse {
  return (
    typeof data === "object" &&
    data !== null &&
    "chats" in data &&
    Array.isArray((data as { chats: unknown }).chats)
  )
}

function isGetChatsInfiniteData(
  data: unknown
): data is { pages: GetChatsResponse[] } {
  return (
    typeof data === "object" &&
    data !== null &&
    "pages" in data &&
    Array.isArray((data as { pages: unknown }).pages) &&
    (data as { pages: unknown[] }).pages.every(isGetChatsResponse)
  )
}

export function chatsFromInfiniteData(data?: {
  pages: GetChatsResponse[]
}): ThinChatSummary[] {
  return data?.pages.flatMap((page) => page.chats) ?? []
}

/** Look up trip.user_created for a chat id from any cached getChats result. */
export function selectChatTripIsUserCreatedFromChatsCache(
  state: RootState,
  chatId: number
): boolean {
  for (const entry of Object.values(state.api.queries)) {
    const data = entry?.data
    if (!isGetChatsInfiniteData(data)) {
      continue
    }
    const chat = chatsFromInfiniteData(data).find(
      (summary) => summary.id === chatId
    )
    if (chat?.trip.user_created) {
      return true
    }
  }

  return false
}

export const {
  useGetActionPillAnswerQuery,
  useGetCollaboratorChatMessagesInfiniteQuery,
  useGetCollaboratorChatsInfiniteQuery,
  useGetEphemeralLocationAnswerQuery,
  useGetEphemeralPlaceQuestionAnswerQuery,
  useGetIdeasChatQuery,
  useGetIdeasQuestionsQuery,
  useOpenCollaboratorChatInGroupMutation,
  usePromoteChatMutation,
  useRefreshFlightSearchMutation,
} = chatsApi
